import React, { useMemo, useSyncExternalStore } from 'react'
import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import Lightbox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/thumbnails.css'
import { getImageDisplayUrl } from '@/services/commonService'

type ImagePreviewState = { open: boolean; images: string[]; index: number }

const listeners = new Set<() => void>()
let current: ImagePreviewState = { open: false, images: [], index: 0 }

const publish = (next: ImagePreviewState) => {
  current = next
  listeners.forEach((l) => l())
}

const subscribe = (onStoreChange: () => void) => {
  listeners.add(onStoreChange)
  return () => {
    listeners.delete(onStoreChange)
  }
}

const cleanImageList = (images: Array<string | null | undefined>): string[] =>
  Array.from(new Set(images.map((s) => String(s ?? '').trim()).filter(Boolean)))

/** Mở lightbox xem ảnh lớn — `images` là URL gốc (chưa qua `getImageDisplayUrl`). */
export const openImagePreview = (images: Array<string | null | undefined>, startSrc?: string | null) => {
  const list = cleanImageList(images)
  if (!list.length) return
  const index = startSrc ? Math.max(0, list.indexOf(startSrc.trim())) : 0
  publish({ open: true, images: list, index })
}

export const closeImagePreview = () => publish({ ...current, open: false })

/** Lightbox dùng chung — mount 1 lần ở gốc app để không bị unmount theo dropdown / dòng bảng. */
export const ImagePreviewHost: React.FC = () => {
  const state = useSyncExternalStore(subscribe, () => current, () => current)
  const slides = useMemo(() => state.images.map((src) => ({ src: getImageDisplayUrl(src) })), [state.images])
  const multi = slides.length > 1

  return (
    <Lightbox
      open={state.open}
      close={closeImagePreview}
      index={state.index}
      slides={slides}
      plugins={multi ? [Zoom, Thumbnails] : [Zoom]}
      carousel={{ finite: !multi }}
      render={multi ? undefined : { buttonPrev: () => null, buttonNext: () => null }}
      controller={{ closeOnBackdropClick: true }}
      zoom={{ maxZoomPixelRatio: 5, scrollToZoom: true }}
      thumbnails={{ position: 'bottom', width: 96, height: 64, border: 2, borderRadius: 4, gap: 12, imageFit: 'cover' }}
    />
  )
}

/** Gom ảnh đại diện + danh sách ảnh của hàng hóa (bỏ trùng, giữ thứ tự). */
export const productImageList = (product: {
  anhDaiDien?: string | null
  hinhAnh?: Array<string | null | undefined> | string | null
} | null | undefined): string[] => {
  if (!product) return []
  const hinhAnh = Array.isArray(product.hinhAnh)
    ? product.hinhAnh
    : typeof product.hinhAnh === 'string'
      ? product.hinhAnh.split(',')
      : []
  return cleanImageList([product.anhDaiDien, ...hinhAnh])
}

type PreviewableImageProps = {
  /** URL gốc của ảnh đang hiển thị. */
  src: string
  /** Bộ ảnh xem trong lightbox (mặc định chỉ `src`). */
  images?: Array<string | null | undefined>
  alt?: string
  sx?: SxProps<Theme>
}

/**
 * Ảnh bấm để xem lớn. Chặn mousedown/click lan lên phần tử cha
 * (dòng bảng, nút chọn hàng, option Autocomplete) để không kích hoạt hành động của cha.
 */
export const PreviewableImage: React.FC<PreviewableImageProps> = ({ src, images, alt = '', sx }) => {
  const stop = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }
  return (
    <Box
      component="img"
      src={getImageDisplayUrl(src)}
      alt={alt}
      draggable={false}
      onMouseDown={stop}
      onClick={(e: React.MouseEvent) => {
        stop(e)
        openImagePreview(images?.length ? images : [src], src)
      }}
      sx={[{ cursor: 'zoom-in' }, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  )
}
